<template>
  <div class="audit-status-page" v-loading="loading">
    <!-- 未选择考试 -->
    <div v-if="!currentExamInfo.id" class="empty-wrap">
      <i class="el-icon-document" style="font-size: 66px; color: #c0c4cc;"></i>
      <div style="margin-top: 20px;">请先在“报名列表”中选择一场考试</div>
    </div>

    <!-- 已选择考试但未提交报名 -->
    <div v-else-if="!registrationInfo || registrationInfo.submitStatus !== 1" class="empty-wrap">
      <i class="el-icon-document" style="font-size: 66px; color: #c0c4cc;"></i>
      <div style="margin-top: 20px;">您尚未提交“{{ currentExamInfo.name }}”的报名信息，暂无审核状态</div>
    </div>

    <template v-else>
      <!-- 考试基本信息 -->
      <el-card class="info-card">
        <div slot="header" class="card-header">
          <span class="exam-name">{{ currentExamInfo.name }}</span>
          <el-tag class="overall-tag" :type="overallTagType" size="medium">{{ overallStatusText }}</el-tag>
        </div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="提交时间">{{ formatDateTime(registrationInfo.submitTime) }}</el-descriptions-item>
          <el-descriptions-item label="报名时间">
            {{ formatDate(currentExamInfo.signStartDate) }} ~ {{ formatDate(currentExamInfo.signEndDate) }}
          </el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 审核进度：按审查「环节」分阶段卡片展示（线上 / 线下各自独立状态），
           线下资格审查按考试配置动态显示或隐藏，避免固定进度条带来的歧义 -->
      <el-card class="info-card">
        <div slot="header" class="card-header"><span>审核进度</span></div>
        <div class="stage-list">
          <div v-for="stage in auditStages" :key="stage.key" class="stage-card" :class="'is-' + stage.state">
            <div class="stage-head">
              <i class="stage-icon" :class="stage.icon"></i>
              <span class="stage-name">{{ stage.name }}</span>
              <el-tag class="stage-tag" size="medium" :type="stage.tagType">{{ stage.statusText }}</el-tag>
            </div>
            <!-- 卡片内容只展示后台配置与审核数据：审核意见、审核状态提示语 -->
            <div class="stage-body">
              <!-- 审核意见：审核不通过 / 不符合条件时必须展示，审核通过填写后才展示 -->
              <p v-if="stage.opinionRequired || stage.opinion" class="stage-opinion">
                <span class="label">{{ stage.opinionLabel }}</span>{{ stage.opinion || "-" }}
              </p>
              <!-- 提示语：取后台「审核状态提示语」配置 -->
              <p v-if="stage.tip" class="stage-tip">
                <span class="tip-label">提示：</span>{{ stage.tip }}
              </p>
            </div>
          </div>
        </div>

        <!-- 终态：全部资格审查完成 -->
        <p v-if="allAuditFinished" class="all-done-tip">
          <i class="el-icon-circle-check"></i>
          {{ finishedTip }}
        </p>

        <!-- 不符合条件：提供快捷返回考生报名信息页的入口，便于完善后重新提交 -->
        <div class="action-block" v-if="showBackToSignUp">
          <el-button type="primary" size="small" @click="goSignUp">
            返回考生报名信息页
          </el-button>
        </div>
      </el-card>

    </template>
  </div>
</template>

<script>
import * as ExamRegistrationApi from "@/api/exam/registration";
import { mapGetters, mapActions } from "vuex";
// 报名审核状态统一映射（与后台字典口径一致）
import { getAuditStatusText, getAuditStatusTagType } from "@/utils/auditStatus";

export default {
  name: "ExamAuditStatus",
  data() {
    return {
      loading: false,
      registrationInfo: null,
    };
  },
  computed: {
    ...mapGetters(["currentExamInfo", "currentExamId"]),
    oneOpinion() {
      return this.registrationInfo?.oneAuditOpinion || "";
    },
    twoOpinion() {
      return this.registrationInfo?.twoAuditOpinion || "";
    },
    // 线上资格审查（始终展示）
    // 状态：0/1 待线上审核、2 线上审核通过、3 线上审核不通过、5 线上审核不符合条件
    // 卡片内容只取自后台配置与审核数据：审核状态(标签) / 备注(审核意见) / 提示语，
    // 不额外拼接前端自行编写的说明文案
    oneStage() {
      const info = this.registrationInfo;
      const status = Number(info?.oneAuditStatus);
      const cfg = this.parseStatusConfig(this.currentExamInfo?.oneAuditStatusConfig);
      const base = {
        key: "one",
        name: "线上资格审查",
        opinionLabel: "审核意见：",
      };
      if (status === 2) {
        return {
          ...base,
          state: "pass",
          icon: "el-icon-circle-check",
          tagType: "success",
          statusText: "线上审核通过",
          // 审核通过：备注填写了才展示
          opinionRequired: false,
          opinion: this.oneOpinion,
          tip: this.pickStatusTip(cfg, "审核通过", 1),
        };
      }
      if (status === 3) {
        return {
          ...base,
          state: "reject",
          icon: "el-icon-circle-close",
          tagType: "danger",
          statusText: "线上审核不通过",
          // 审核不通过：备注必须展示
          opinionRequired: true,
          opinion: this.oneOpinion,
          tip: this.pickStatusTip(cfg, "审核不通过", 1),
        };
      }
      if (status === 5) {
        return {
          ...base,
          state: "unqualified",
          icon: "el-icon-warning-outline",
          tagType: "warning",
          statusText: "线上审核不符合条件",
          // 不符合条件：备注必须展示
          opinionRequired: true,
          opinion: this.oneOpinion,
          tip: this.pickStatusTip(cfg, "不符合条件", 1),
        };
      }
      // 待线上审核（含自动审核尚未落结果的瞬间）
      return {
        ...base,
        state: "wait",
        icon: "el-icon-time",
        tagType: "",
        statusText: "待线上审核",
        opinionRequired: false,
        opinion: "",
        tip: "",
      };
    },
    // 线下资格审查（仅在考试启用线下资格审查时展示）
    // 状态：1 待线下资格审查、2 线下审核通过、4 线下审核不通过、5 线下审核不符合条件、6 线下未到场
    twoStage() {
      const info = this.registrationInfo;
      const status = Number(info?.twoAuditStatus);
      const cfg = this.parseStatusConfig(this.currentExamInfo?.twoAuditStatusConfig);
      const base = {
        key: "two",
        name: "线下资格审查",
        opinionLabel: "审核意见：",
      };
      if (status === 2) {
        return {
          ...base,
          state: "pass",
          icon: "el-icon-circle-check",
          tagType: "success",
          statusText: "线下审核通过",
          // 审核通过：备注填写了才展示
          opinionRequired: false,
          opinion: this.twoOpinion,
          tip: this.pickStatusTip(cfg, "审核通过", 2),
        };
      }
      if (status === 4) {
        return {
          ...base,
          state: "reject",
          icon: "el-icon-circle-close",
          tagType: "danger",
          statusText: "线下审核不通过",
          // 审核不通过：备注必须展示
          opinionRequired: true,
          opinion: this.twoOpinion,
          tip: this.pickStatusTip(cfg, "审核不通过", 2),
        };
      }
      if (status === 5) {
        return {
          ...base,
          state: "unqualified",
          icon: "el-icon-warning-outline",
          tagType: "warning",
          statusText: "线下审核不符合条件",
          // 不符合条件：备注必须展示
          opinionRequired: true,
          opinion: this.twoOpinion,
          tip: this.pickStatusTip(cfg, "不符合条件", 2),
        };
      }
      if (status === 6) {
        // 未到场：提示语取“线下资格审查”配置中含“未到场”的那一项（默认未配置，则为空）
        const absentKey = Object.keys(cfg).find((k) => k.indexOf("未到场") > -1);
        return {
          ...base,
          state: "absent",
          icon: "el-icon-warning-outline",
          tagType: "warning",
          statusText: "线下未到场",
          opinionRequired: false,
          opinion: this.twoOpinion,
          tip: absentKey ? cfg[absentKey] : "",
        };
      }
      // 待线下资格审查
      return {
        ...base,
        state: "wait",
        icon: "el-icon-time",
        tagType: "",
        statusText: "待线下资格审查",
        opinionRequired: false,
        opinion: "",
        tip: "",
      };
    },
    // 心理测试环节（仅在考试启用心理测试、且资格审查已通过后展示）
    // 状态：0 待编排、1 待参加测试、2 合格、3 不合格、4 缺考
    // 提示语统一取后台「心理测试 - 状态提示语」配置，
    // 键名与后台考试编辑弹窗一致：待编排 / 待参加测试 / 合格 / 不合格 / 缺考
    psyStage() {
      const status = Number(this.registrationInfo?.psyStatus ?? 0);
      const stageName = this.psyStageName;
      const cfg = this.parseStatusConfig(this.currentExamInfo?.psyAuditStatusConfig);
      // 后台未配置时的兜底提示语（与后台默认值保持一致）
      const fallbackTip = {
        待编排: `资格审查通过后，请等待${stageName}考场编排。`,
        待参加测试: `${stageName}准考证发布后，请按时参加${stageName}。`,
        合格: "",
        不合格: `您本次${stageName}未达标，取消本次笔试考试资格，后续流程终止。`,
        缺考: `您未按时参加${stageName}，取消本次笔试考试资格，后续流程终止。`,
      };
      const stateMap = {
        0: { state: "wait", icon: "el-icon-time", tagType: "", statusText: "待编排", cfgKey: "待编排" },
        1: { state: "doing", icon: "el-icon-time", tagType: "primary", statusText: "待参加测试", cfgKey: "待参加测试" },
        2: { state: "pass", icon: "el-icon-circle-check", tagType: "success", statusText: `${stageName}合格`, cfgKey: "合格" },
        3: { state: "reject", icon: "el-icon-circle-close", tagType: "danger", statusText: `${stageName}不合格`, cfgKey: "不合格" },
        4: { state: "absent", icon: "el-icon-warning-outline", tagType: "warning", statusText: `${stageName}缺考`, cfgKey: "缺考" },
      };
      const cur = stateMap[status] || stateMap[0];
      return {
        key: "psy",
        name: stageName,
        opinionLabel: "审核意见：",
        opinionRequired: false,
        opinion: "",
        ...cur,
        tip: (cfg && cfg[cur.cfgKey]) || fallbackTip[cur.cfgKey] || "",
      };
    },
    // 阶段卡片列表：线上审查始终展示；线下审查仅在考试启用时展示；心理测试仅在启用且已进入该环节时展示
    auditStages() {
      const info = this.registrationInfo;
      if (!info) return [];
      const stages = [this.oneStage];
      if (Number(info.twoChkFlag) === 1) stages.push(this.twoStage);
      if (this.showPsyStage) stages.push(this.psyStage);
      return stages;
    },
    // 心理测试环节名称：按考试自定义，未配置时按默认名称展示
    psyStageName() {
      return this.currentExamInfo?.psyStageName || "心理测试";
    },
    // 考试是否启用心理测试环节（取自考试配置 psyChkFlag）
    psyEnabled() {
      return Number(this.currentExamInfo?.psyChkFlag) === 1;
    },
    // 是否展示心理测试卡片：启用心理测试的前提下，资格审查通过后进入该环节；
    // 若已有心理测试进度（已编排/已出结果）则照常展示，避免结果出来后卡片消失
    showPsyStage() {
      if (!this.psyEnabled) return false;
      const info = this.registrationInfo;
      if (!info) return false;
      return Number(info.auditStatus) === 2 || Number(info.psyStatus) > 0;
    },
    // 终态：报名整体审核通过
    allAuditFinished() {
      return Number(this.registrationInfo?.auditStatus) === 2;
    },
    // 终态提示：启用心理测试的考试，资格审查通过后还要参加心理测试，文案需要区分
    finishedTip() {
      if (!this.psyEnabled) return "全部资格审查已完成，请等待打印准考证。";
      const psyStatus = Number(this.registrationInfo?.psyStatus ?? 0);
      if (psyStatus === 3 || psyStatus === 4) {
        return `您的${this.psyStageName}结果已出，本次报名流程已终止。`;
      }
      return `全部资格审查已完成，${this.psyStageName}准考证发布后可在“准考证下载”页下载打印。`;
    },
    // 整体审核状态：统一走报名状态枚举映射（见 @/utils/auditStatus）
    overallStatusText() {
      if (!this.registrationInfo) return "-";
      return getAuditStatusText(this.registrationInfo.auditStatus);
    },
    overallTagType() {
      if (!this.registrationInfo) return "primary";
      return getAuditStatusTagType(this.registrationInfo.auditStatus);
    },
    // 仅「线上资格审查不符合条件」时，允许考生返回报名页完善并重新提交；
    // 「线下资格审查不符合条件」（twoAuditStatus === 5）报名信息已锁定，不再提供返回入口
    showBackToSignUp() {
      const info = this.registrationInfo;
      if (!info) return false;
      return (
        Number(info.auditStatus) === 5 &&
        Number(info.twoAuditStatus) !== 5
      );
    },
    genderText() {
      const g = this.registrationInfo?.gender;
      return g === "1" || g === 1 ? "男" : g === "2" || g === 2 ? "女" : "-";
    },
    postText() {
      const list = this.currentExamInfo?.postList || [];
      const item = list.find((p) => p.id === this.registrationInfo?.postId);
      return item ? item.name : this.registrationInfo?.postId || "-";
    },
    customFields() {
      const exclude = ["headImg", "name", "gender", "idCard", "postId"];
      return (this.registrationInfo?.fields || []).filter(
        (f) => !exclude.includes(f.columnField),
      );
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    ...mapActions(["fetchExamInfoById"]),
    // 快捷返回考生报名信息页（不符合条件时用于完善后重新提交）
    goSignUp() {
      this.$router.push({ name: "exam-sign-up" });
    },
    // 解析考试配置中“审核状态提示语”，统一成 {状态名: 提示语}：
    // 兼容字符串 JSON、对象、以及旧版数组结构 [{name, tip}]
    parseStatusConfig(val) {
      const map = {};
      if (!val) return map;
      let raw = val;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
        } catch (e) {
          return map;
        }
      }
      if (Array.isArray(raw)) {
        raw.forEach((i) => {
          if (i && i.name != null) map[i.name] = i.tip ?? "";
        });
        return map;
      }
      return raw && typeof raw === "object" ? { ...raw } : map;
    },
    // 考试未保存配置时的兜底提示语：与后台考试编辑页一致，线上/线下各一套
    pickStatusTip(cfg, key, stage = 1) {
      const oneFallback = {
        审核通过: "审核通过后，不能修改报考岗位和个人信息",
        审核不通过: "考生不符合本次招聘的岗位要求，将不能重新报考本次招聘的岗位。",
        不符合条件: "在报名期间内，考生可重新完善信息或重新选岗报名",
      };
      const twoFallback = {
        审核通过: "审核通过",
        审核不通过: "考生不符合本次招聘的岗位要求，将不能报考本次招聘的岗位。",
        不符合条件: "请考生在线下资格审查截至日前重新携带资料到线下资格审查点审查",
      };
      const fallback = stage === 2 ? twoFallback : oneFallback;
      if (cfg && cfg[key]) return cfg[key];
      const matched = Object.keys(cfg || {}).filter((k) => {
        if (!cfg[k]) return false;
        if (key === "审核通过") return k.indexOf("不通过") === -1 && k.indexOf("通过") > -1;
        if (key === "审核不通过") return k.indexOf("不通过") > -1;
        if (key === "不符合条件") return k.indexOf("不符合条件") > -1;
        return false;
      });
      return matched.length ? cfg[matched[0]] : fallback[key] || "";
    },
    async fetchData() {
      if (!this.currentExamId) return;
      this.loading = true;
      try {
        // 保证 currentExamInfo 与当前考试一致（提示语配置取自考试信息）
        if (this.currentExamInfo?.id !== this.currentExamId) {
          await this.fetchExamInfoById();
        }
        const res = await ExamRegistrationApi.get({ examId: this.currentExamId });
        this.registrationInfo = res;
      } catch (e) {
        this.$message.error("审核信息加载失败");
      } finally {
        this.loading = false;
      }
    },
    toDate(v) {
      if (v === null || v === undefined || v === "") return null;
      if (typeof v === "number") return new Date(v);
      if (/^\d{10,13}$/.test(String(v))) return new Date(Number(v));
      return new Date(String(v).replace(" ", "T"));
    },
    formatDate(v) {
      const d = this.toDate(v);
      if (!d || isNaN(d.getTime())) return "-";
      const pad = (n) => (n < 10 ? "0" + n : n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatDateTime(v) {
      const d = this.toDate(v);
      if (!d || isNaN(d.getTime())) return "-";
      const pad = (n) => (n < 10 ? "0" + n : n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
  },
};
</script>

<style scoped lang="scss">
.audit-status-page {
  padding: 24px;
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 200px);
  color: #909399;
}

.info-card {
  margin-bottom: 20px;

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .exam-name {
      font-size: 18px;
      font-weight: bold;
      color: #303133;
    }

    /* 整体审核状态：字号放大，便于考生一眼看清 */
    .overall-tag {
      height: 38px;
      line-height: 36px;
      padding: 0 16px;
      font-size: 20px;
    }
  }
}

/* 阶段卡片：每个审查环节一张卡片，按状态着色 */
.stage-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stage-card {
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-left-width: 4px;
  border-radius: 6px;

  &.is-wait {
    border-left-color: #c0c4cc;

    .stage-icon {
      color: #c0c4cc;
    }
  }

  &.is-pass {
    background: #f0f9eb;
    border-left-color: #67c23a;

    .stage-icon {
      color: #67c23a;
    }
  }

  &.is-reject {
    background: #fef0f0;
    border-left-color: #f56c6c;

    .stage-icon {
      color: #f56c6c;
    }
  }

  &.is-unqualified,
  &.is-absent {
    background: #fdf6ec;
    border-left-color: #e6a23c;

    .stage-icon {
      color: #e6a23c;
    }
  }

  /* 心理测试「待参加测试」：已编排、等待参加，用蓝色区分于「待编排」的灰色 */
  &.is-doing {
    background: #ecf5ff;
    border-left-color: #409eff;

    .stage-icon {
      color: #409eff;
    }
  }

  .stage-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .stage-icon {
      font-size: 18px;
    }

    .stage-name {
      font-size: 15px;
      font-weight: bold;
      color: #303133;
    }

    /* 各环节审核状态标签：字号放大 */
    .stage-tag {
      height: 36px;
      line-height: 34px;
      padding: 0 16px;
      font-size: 18px;
      margin-left: auto;
    }
  }

  .stage-body {
    margin-top: 8px;
    padding-left: 26px;

    p {
      margin: 0 0 6px;
      font-size: 13px;
      line-height: 1.8;
      color: #606266;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .label,
    .tip-label {
      color: #909399;
    }

    .stage-tip {
      color: #409eff;
    }
  }
}

/* 终态：全部资格审查完成 */
.all-done-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 16px 0 0;
  padding: 10px 12px;
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 6px;
  font-size: 14px;
  color: #67c23a;
}

.action-block {
  margin-top: 24px;
  text-align: center;
}

/* 手机端适配 */
@media screen and (max-width: 768px) {
  .audit-status-page {
    padding: 12px;
  }

  .stage-card .stage-body {
    padding-left: 0;
  }
}
</style>
